import type { Money } from '@dastarhan/contracts';
import { formatMoney } from './money';
import { lineSum, type ItemModifier } from './order';

/**
 * Раскладка чека в моноширинные строки. Чистая функция: одна и та же
 * лента уходит в браузерную печать сейчас и в ESC/POS-байты, когда
 * появится транспорт к принтеру. Ширина 32 — стандарт 58-мм ленты.
 */

export interface ReceiptInput {
  place: string;
  number: number;
  cashier: string;
  /** официант, ведший стол — печатается отдельной строкой */
  waiter?: string;
  table?: string;
  /** когда стол открыт: гость видит «Открыт 15:03 · Закрыт 15:48» */
  openedAt?: Date;
  /** копия печатается с пометкой — гость не примет её за второй чек */
  copy?: boolean;
  at: Date;
  items: { name: string; qty: number; unitPrice: Money; modifiers: ItemModifier[] }[];
  /** предварительный счёт: без оплат и с честной пометкой */
  precheck?: boolean;
  /** имя гостя — на пречеке приятно видеть себя */
  guestName?: string;
  /** зачтённая предоплата брони */
  deposit?: Money;
  /** язык печати; по умолчанию русский */
  lang?: ReceiptLang;
  /** печатать приглашение оценить (QR идёт отдельным аргументом печати) */
  feedbackQr?: boolean;
  /** подарочный код для друга — печатается в подвале */
  referralCode?: { code: string; percent: number };
  /** подвал заведения: адрес, телефон, БИН — печатаются в конце ленты */
  address?: string;
  phone?: string;
  bin?: string;
  discount?: Money;
  /** процент скидки: гость должен видеть, сколько ему уступили */
  discountPct?: number;
  /** сбор за обслуживание и его процент */
  service?: Money;
  servicePct?: number;
  promos?: { name: string; amount: Money }[];
  fiscalUrl?: string;
  total: Money;
  payments: { method: 'CASH' | 'CARD' | 'KASPI_QR'; amount: Money; tenderedCash?: Money }[];
}

/** Чек говорит на языке гостя: переключатель живёт на кассе. */
export type ReceiptLang = 'ru' | 'kk';
const L: Record<ReceiptLang, Record<string, string>> = {
  ru: {
    precheck: 'ПРЕДВАРИТЕЛЬНЫЙ СЧЁТ', toCheck: 'к чеку №', check: 'Чек №',
    cashier: 'Кассир', waiter: 'Официант', table: 'Стол', guest: 'Гость',
    opened: 'Открыт', closed: 'Закрыт',
    colItem: 'Блюдо', colQty: 'Кол', colSum: 'Сумма',
    each: 'по', currency: 'ТЕНГЕ', copy: '*** КОПИЯ ***',
    subtotal: 'ПОДЫТОГ', discount: 'Скидка', total: 'ИТОГО', service: 'Обслуживание',
    deposit: 'Предоплата (бронь)', due: 'К ОПЛАТЕ',
    notFiscal: 'Не является фискальным чеком', waitPay: 'Ждём вас для оплаты',
    received: 'Получено', change: 'Сдача', fiscal: 'Чек ОФД:',
    allIn: 'ВСЕ СУММЫ В ТЕНГЕ',
    gift: '🎁 Код для друга:', giftNote: 'скидка {p}% на первый заказ',
    fb: 'Как вам? Оцените по QR ↓',
    CASH: 'Наличные', CARD: 'Карта', KASPI_QR: 'Kaspi QR',
  },
  kk: {
    precheck: 'АЛДЫН АЛА ШОТ', toCheck: '№ чекке', check: 'Чек №',
    cashier: 'Кассир', waiter: 'Даяшы', table: 'Үстел', guest: 'Қонақ',
    opened: 'Ашылды', closed: 'Жабылды',
    colItem: 'Тағам', colQty: 'Сан', colSum: 'Сома',
    each: 'данасы', currency: 'ТЕҢГЕ', copy: '*** КӨШІРМЕ ***',
    subtotal: 'АРАЛЫҚ ЖИЫН', discount: 'Жеңілдік', total: 'БАРЛЫҒЫ', service: 'Қызмет көрсету',
    deposit: 'Алдын ала төлем (брондау)', due: 'ТӨЛЕУГЕ',
    notFiscal: 'Фискалдық чек емес', waitPay: 'Төлеуге күтеміз',
    received: 'Алынды', change: 'Қайтарым', fiscal: 'ОФД чегі:',
    allIn: 'БАРЛЫҚ СОМА — ТЕҢГЕМЕН',
    gift: '🎁 Досқа арналған код:', giftNote: 'алғашқы тапсырысқа {p}% жеңілдік',
    fb: 'Қалай болды? QR арқылы бағалаңыз ↓',
    CASH: 'Қолма-қол', CARD: 'Карта', KASPI_QR: 'Kaspi QR',
  },
};

/** Две колонки на всю ширину; правая (сумма) неприкосновенна. */
export function padLR(left: string, right: string, width: number): string {
  const space = width - left.length - right.length;
  if (space >= 1) return left + ' '.repeat(space) + right;
  const leftRoom = Math.max(2, width - right.length - 1);
  return left.slice(0, leftRoom - 1) + '… ' + right;
}

export function center(s: string, width: number): string {
  if (s.length >= width) return s.slice(0, width);
  const pad = Math.floor((width - s.length) / 2);
  return ' '.repeat(pad) + s;
}

/**
 * Разрядка названия заведения: «К А Ф Е  « Д А С Т А Р Х А Н »».
 * На термоленте нет ни кегля, ни насыщенности — вес даёт только
 * пробел между буквами. Если разрядка не влезает, печатаем как есть.
 */
export function spaced(s: string, width: number): string {
  const wide = [...s].join(' ');
  return wide.length <= width ? wide : s;
}

/** Перенос по словам; слово длиннее строки режется жёстко. */
export function wrap(s: string, width: number): string[] {
  const out: string[] = [];
  let line = '';
  for (const word of s.split(/\s+/)) {
    let w = word;
    while (w.length > width) {
      if (line) { out.push(line); line = ''; }
      out.push(w.slice(0, width));
      w = w.slice(width);
    }
    if (!line) line = w;
    else if (line.length + 1 + w.length <= width) line += ' ' + w;
    else { out.push(line); line = w; }
  }
  if (line) out.push(line);
  return out;
}

export function receiptLines(r: ReceiptInput, width = 32): string[] {
  const hr = '─'.repeat(width);
  const heavy = '═'.repeat(width);   // тяжёлая линия — только перед итогом
  const out: string[] = [];
  const t = L[r.lang ?? 'ru']!;
  /*
   * Деньги в теле чека — чистые числа: валюта названа один раз,
   * строкой «ТЕНГЕ» под итогом, как принято на хороших чеках.
   * Значок ₸ в каждой строке только съедал ширину и, будучи шире
   * одного байта в кодировке принтера, ломал раскладку
   */
  const N = (v: Money) => formatMoney(v).replace(' ₸', '');

  // ── Шапка по канону: заведение, «Чек · Стол», времена, люди ──
  out.push(center(spaced(r.place, width), width));
  if (r.precheck) {
    out.push(center(t.precheck!, width));
    out.push(center(`${t.toCheck} ${r.number}`, width));
  } else {
    out.push('');
    out.push(padLR(`${t.check}${r.number}`, r.table ? `${t.table} ${r.table}` : '', width));
  }
  const date = r.at.toLocaleDateString('ru-RU');
  const closeTime = r.at.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  const openTime = r.openedAt
    ? r.openedAt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    : null;
  // «Открыт 15:03  Закрыт 15:48» влезает на 80-мм ленте; узкой —
  // короткая пара «дата | время», ничего не режем многоточием
  const times = openTime && width >= 42
    ? `${t.opened} ${openTime}  ${t.closed} ${closeTime}`
    : closeTime;
  out.push(padLR(date, times, width));
  out.push(`${t.cashier}: ${r.cashier}`);
  if (r.waiter && r.waiter !== r.cashier) out.push(`${t.waiter}: ${r.waiter}`);
  if (r.guestName) out.push(`${t.guest}: ${r.guestName}`);
  if (r.precheck && r.table) out.push(`${t.table}: ${r.table}`);
  if (r.copy) { out.push(''); out.push(center(t.copy!, width)); }
  out.push(hr);

  // ── Таблица позиций: Блюдо | Кол | Сумма. Правые колонки
  //    неприкосновенны; их ширина — от самых длинных значений ──
  const qtyStr = (q: number) => (Number.isInteger(q) ? String(q) : String(q));
  const sumW = Math.max(t.colSum!.length,
    ...r.items.map((i) => N(lineSum(i)).length));
  const qtyW = Math.max(t.colQty!.length, ...r.items.map((i) => qtyStr(i.qty).length));
  const nameW = Math.max(8, width - qtyW - sumW - 4);
  const row = (name: string, qty: string, sum: string) =>
    name.padEnd(nameW).slice(0, nameW) + '  '
    + qty.padStart(qtyW) + '  ' + sum.padStart(sumW);
  out.push(row(t.colItem!, t.colQty!, t.colSum!));
  out.push(hr);
  for (const i of r.items) {
    const [first = '', ...rest] = wrap(i.name, nameW);
    out.push(row(first, qtyStr(i.qty), N(lineSum(i))));
    for (const cont of rest) out.push(cont);
    const delta = i.modifiers.reduce((s, m) => s + m.priceDelta, 0);
    if (i.qty !== 1) {
      out.push(`  ${t.each} ${N(i.unitPrice + delta)}`);
    }
    for (const m of i.modifiers) {
      out.push(`  + ${m.name}${m.priceDelta ? ` (${N(m.priceDelta)})` : ''}`);
    }
  }

  // ── Итоговый блок: тяжёлая линия, потом только деньги ──
  out.push(heavy);
  const hasPromos = (r.promos?.length ?? 0) > 0;
  const hasService = (r.service ?? 0) > 0;
  if ((r.discount && r.discount > 0) || hasPromos || hasService) {
    const subtotal = r.items.reduce((s, i) => s + lineSum(i), 0);
    out.push(padLR(t.subtotal!, N(subtotal), width));
    for (const p of r.promos ?? []) {
      out.push(padLR(p.name, `-${N(p.amount)}`, width));
    }
    if (r.discount && r.discount > 0) {
      // Процент рядом с суммой: «Скидка 10%   -500 ₸». Гость видит
      // не только сколько сняли, но и по какому праву — это снимает
      // половину вопросов у кассы
      const label = r.discountPct && r.discountPct > 0
        ? `${t.discount!} ${r.discountPct}%`
        : t.discount!;
      out.push(padLR(label, `-${N(r.discount)}`, width));
    }
    if (hasService) {
      const label = r.servicePct && r.servicePct > 0
        ? `${t.service!} ${r.servicePct}%`
        : t.service!;
      out.push(padLR(label, `+${N(r.service!)}`, width));
    }
  }
  out.push(padLR(t.total!, N(r.total), width));
  if (r.deposit && r.deposit > 0) {
    out.push(padLR(t.deposit!, `-${N(r.deposit)}`, width));
    out.push(padLR(t.due!, N(Math.max(0, r.total - r.deposit)), width));
  }
  /*
   * Финальная денежная строка чека — ИТОГО (или К ОПЛАТЕ при
   * предоплате). Валюта — центрированной подписью, как на X/Z и
   * актах: строка «ТЕНГЕ + сумма» читалась вторым итогом и путала
   */
  out.push(center(`${t.allIn!}`, width));
  if (r.precheck) {
    out.push('');
    out.push(center(t.notFiscal!, width));
    out.push(center(t.waitPay!, width));
    out.push(hr);
    out.push(center('Рақмет! Спасибо!', width));
    return foot(out, r, width);
  }
  out.push('');
  for (const p of r.payments) {
    out.push(padLR(t[p.method]!, N(p.amount), width));
    if (p.method === 'CASH' && p.tenderedCash !== undefined && p.tenderedCash > p.amount) {
      out.push(padLR(t.received!, N(p.tenderedCash), width));
      out.push(padLR(t.change!, N(p.tenderedCash - p.amount), width));
    }
  }
  out.push(hr);
  if (r.fiscalUrl) {
    out.push(t.fiscal!);
    for (const l of wrap(r.fiscalUrl, width)) out.push(l);
    out.push(hr);
  }
  if (r.feedbackQr) {
    out.push(center(t.fb!, width));
    out.push(hr);
  }
  if (r.referralCode) {
    out.push(center(t.gift!, width));
    out.push(center(r.referralCode.code, width));
    out.push(center(t.giftNote!.replace('{p}', String(r.referralCode.percent)), width));
    out.push(hr);
  }
  out.push(center('Рақмет! Спасибо!', width));
  return foot(out, r, width);
}

/**
 * Подвал и подача ленты. Адрес, телефон и БИН — мелким блоком в самом
 * низу: гость уносит чек с собой, и по нему находят заведение.
 * Две пустые строки в конце — чтобы нож отрезал не по «Спасибо».
 */
function foot(out: string[], r: ReceiptInput, width: number): string[] {
  const tail = [r.address, r.phone, r.bin && `БИН ${r.bin}`].filter(Boolean) as string[];
  if (tail.length > 0) {
    out.push('');
    for (const s of tail) for (const l of wrap(s, width)) out.push(center(l, width));
  }
  out.push('');
  out.push('');
  return out;
}
